import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ICreateCaixa,
  ICreateCaixaResponse,
  ICreateMovimentacaoCaixa,
  ICreateMovimentacaoCaixaResponse,
  IGetCaixaAtualResponse,
  IGetCaixaByIdResponse,
  IGetCaixaPagamentosByFilterPaginatedParams,
  IGetCaixaPagamentosByFilterPaginatedResponse,
  IGetCaixasMonthYearDataAberturaResponse,
  IGetCaixaTotalizadorByIdParams,
  IGetCaixaTotalizadorByIdResponse,
  IGetMovimentacaoCaixaById,
  IGetReceiptResponse,
  IPatchCaixaStatusParams,
  IPatchCaixaStatusResponse,
  IUpdateMovimentacaoCaixa
} from './caixa.model';

@Injectable({
  providedIn: 'root'
})
export class CaixaService {
  constructor(private http: HttpClient) {}

  public CreateCaixa(
    body: ICreateCaixa,
    guid: string
  ): Observable<ICreateCaixaResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      IdempotencyKey: guid
    });
    return this.http.post<any>(
      `${environment.baseURL}Caixa/CreateCaixa`,
      body,
      { headers: headers }
    );
  }

  public CreateMovimentacaoCaixa(
    body: ICreateMovimentacaoCaixa,
    guid: string
  ): Observable<ICreateMovimentacaoCaixaResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      IdempotencyKey: guid
    });
    return this.http.post<any>(
      `${environment.baseURL}Caixa/CreateMovimentacaoCaixa`,
      body,
      { headers: headers }
    );
  }

  public GetCaixaPagamentosByFilterPaginated(
    params: IGetCaixaPagamentosByFilterPaginatedParams
  ): Observable<IGetCaixaPagamentosByFilterPaginatedResponse> {
    params.PAGE_NUMBER = params.PAGE_NUMBER || 1;

    const invalidParams = ['', null, undefined, NaN, 0];

    const validParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => !invalidParams.includes(value)
      )
    );

    return this.http.get<any>(
      `${environment.baseURL}Caixa/GetCaixaPagamentosByFilterPaginated`,
      { params: validParams }
    );
  }

  public GetCaixaMovimentacoesExcel(
    params: IGetCaixaPagamentosByFilterPaginatedParams
  ): Observable<IGetReceiptResponse> {
    // Mesma regra da listagem: 0 / vazio = sem filtro
    const invalidParams = ['', null, undefined, NaN, 0];
    const validParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => !invalidParams.includes(value as any)
      )
    );
    return this.http.get<IGetReceiptResponse>(
      `${environment.baseURL}Caixa/GetCaixaMovimentacoesExcel`,
      { params: validParams }
    );
  }

  public GetCaixaById(PK_CAIX: number): Observable<IGetCaixaByIdResponse> {
    return this.http.get<any>(
      `${environment.baseURL}Caixa/GetCaixaById?PK_CAIX=${PK_CAIX}`
    );
  }

  public GetCaixaTotalizadorById(
    params: IGetCaixaTotalizadorByIdParams
  ): Observable<IGetCaixaTotalizadorByIdResponse> {
    const invalidParams = ['', null, undefined, NaN, 0];
    const validParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => !invalidParams.includes(value)
      )
    );

    return this.http.get<any>(
      `${environment.baseURL}Caixa/GetCaixaTotalizadorById`,
      {
        params: validParams
      }
    );
  }

  public GetMovimentacaoCaixaById(
    PK_MOVM_CAIX: number
  ): Observable<IGetMovimentacaoCaixaById> {
    return this.http.get<any>(
      `${environment.baseURL}Caixa/GetMovimentacaoCaixaById?PK_MOVM_CAIX=${PK_MOVM_CAIX}`
    );
  }

  public GetCaixaAtual(nuTipoCaix?: number): Observable<IGetCaixaAtualResponse | null> {
    const params: Record<string, number> = {};
    if (nuTipoCaix) {
      params['NU_TIPO_CAIX'] = nuTipoCaix;
    }

    return this.http.get<IGetCaixaAtualResponse | null>(
      `${environment.baseURL}Caixa/GetCaixaAtual`,
      { params }
    );
  }

  public GetCaixas(): Observable<IGetCaixasMonthYearDataAberturaResponse[]> {
    return this.http.get<IGetCaixasMonthYearDataAberturaResponse[]>(
      `${environment.baseURL}Caixa/GetCaixas`
    );
  }

  public GetCaixasMonthYearDataAbertura(
    NU_MONTH: number,
    NU_YEAR: number
  ): Observable<IGetCaixasMonthYearDataAberturaResponse[]> {
    return this.http.get<any>(
      `${environment.baseURL}Caixa/GetCaixasMonthYearDataAbertura?NU_MONTH=${NU_MONTH}&NU_YEAR=${NU_YEAR}`
    );
  }

  public GetReceiptCaixaById(
    PK_CAIX: number,
    FL_TIPO_DOCU: number = 0
  ): Observable<IGetReceiptResponse> {
    return this.http.get<any>(
      `${environment.baseURL}Caixa/GetReceiptCaixaById?PK_CAIX=${PK_CAIX}&FL_TIPO_DOCU=${FL_TIPO_DOCU}&ReportDocumentType=0`
    );
  }

  public GetReceiptSuprimentoSangriaById(
    PK_MOVM_CAIX: number,
    FL_TIPO_DOCU: number,
    NU_MOVM_CAIX: number
  ): Observable<IGetReceiptResponse> {
    const url = `${environment.baseURL}Caixa/GetReceiptSuprimentoSangriaById`;
    const params: any = {
      PK_MOVM_CAIX: 0,
      FL_TIPO_DOCU: 0,
      NU_MOVM_CAIX: 1
    };

    params.PK_MOVM_CAIX = PK_MOVM_CAIX;
    params.FL_TIPO_DOCU = FL_TIPO_DOCU;
    params.NU_MOVM_CAIX = NU_MOVM_CAIX;

    return this.http.get<any>(url, { params });
  }

  public PatchCaixaStatus(
    params: IPatchCaixaStatusParams
  ): Observable<IPatchCaixaStatusResponse> {
    return this.http.patch<any>(
      `${environment.baseURL}Caixa/PatchCaixaStatus`,
      {},
      { params: { ...params } }
    );
  }

  public UpdateMovimentacaoCaixa(
    id: number,
    body: IUpdateMovimentacaoCaixa
  ): Observable<any> {
    return this.http.put<any>(
      `${environment.baseURL}Caixa/UpdateMovimentacaoCaixa?id=${id}`,
      body
    );
  }
}
